<docs>
  # Inactivity screen
  > The Basic Mobile screen
  
  @author nicolas husson <nicolas@kffein.com>
</docs>

<script lang="ts">
import { templateRef } from "@vueuse/core";
import ArrowSVG from "assets/svg/arrow.svg?raw";
import RefreshSVG from "assets/svg/refresh.svg?raw";
import { each } from "lodash";
import { store as interfaceStore } from 'plugins/store/interface';
import useFlow from 'utils/flow';
import { gsap } from "vendors/gsap";
import { defineComponent } from 'vue';

export default defineComponent({
  name: "UiInactivityScreen",
  emits: ['continue'],
  setup(orops, { emit }) {
    const { onInitUi } = useFlow();
    const el = templateRef<HTMLElement>("el");
    
    let installRedirectTimer : any = null;

    onInitUi(() => {
      animate();

      if(interfaceStore.state.supports.install) {
        installRedirectTimer = setTimeout(() => {
          onRefresh();
        }, 15 * 60 *  1000);
      }
    });

    const animate = () => {
      gsap.timeline()
        .add(() => {
          const elmts = ['.page-wrapper', '.TheHeader', '.TheNavigation', ".headerThematique"];
          each(elmts, elmt => {
            document.querySelector(elmt)?.classList.add("-blurry");
          });
        }, 0)
        .fromTo(el.value.querySelector(".background"), { opacity: 0 }, { opacity: 1, duration: 1.5, ease: "power4.inOut" }, 0)
        .fromTo(el.value.querySelectorAll(".h2,.subtitle,.buttons"), { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 1.5, ease: "power4.out", stagger: 0.5 }, 1.5)
    }

    const onRefresh = () => {
      if(interfaceStore.state.supports.install) clearTimeout(installRedirectTimer);

      document.location.href = "/";
    }

    const onContinue = () => {
      emit('continue');

      if(interfaceStore.state.supports.install) clearTimeout(installRedirectTimer);

      const elmts = ['.page-wrapper', '.TheHeader', '.TheNavigation', ".headerThematique"];
      each(elmts, elmt => {
        document.querySelector(elmt)?.classList.remove("-blurry");
      });
    }

    return {
      onRefresh,
      onContinue,
      ArrowSVG,
      RefreshSVG
    }
  }
});
</script>

<template>
  <section ref="el"
           data-variant="university"
           class="inactivity"
  >
    <div class="background" />
    <div class="wrapper">
      <h2 class="h2" v-html="'Vous êtes<br/>toujours là?'" />
      <p class="subtitle" v-html="'Poursuivez votre découverte ou recommencez<br/>l\'exploration depuis le début'" />
      <div class="buttons">
        <ui-button :label="'Recommencer'"
                   theme="primary"
                   class="refresh"
                   :icon="RefreshSVG"
                   tag="a"
                   variant="inactivity"
                   @click="onRefresh"
        />
        <ui-button :label="'Continuer'"
                   theme="primary"
                   class="continue"
                   :icon="ArrowSVG"
                   tag="a"
                   variant="inactivity"
                   @click="onContinue"
        />
      </div>
    </div>
  </section>
</template>

<style lang="stylus" scoped>
  .inactivity
    fixed 0
    color red
    z-index $z-burger + 1
    f(center, $direction:column)
    text-align center
    user-select none

  .h2
    text-transform uppercase
    addVariantProps(color, false) 
    margin-bottom 40px

  .subtitle
    text-transform uppercase
    addVariantProps(color, false)

  .buttons
    margin-top 60px
    .refresh
      margin-right 30px
      :deep()
        .inner
          .icon
            transform scaleX(-1)
    .continue
      :deep()
        .inner
          .icon
            transform scaleX(-1)

  .background
    absolute 0
    size 100%
    background-color rgba(229, 240, 248, .8)
    z-index -1
</style>
